import type { RequestSummary } from "@hoffman-support/contracts";
import { useEffect, useState } from "react";
import { listRequests } from "../api";

export const STATUS_LABELS: Record<RequestSummary["status"], string> = {
  new: "Received",
  in_progress: "In progress",
  waiting_on_user: "Waiting on you",
  resolved: "Resolved, please confirm",
  closed: "Closed",
  cancelled: "Cancelled",
  withdrawn: "Withdrawn",
};

export function RequestList() {
  const [items, setItems] = useState<RequestSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  async function load(next?: string) {
    setState("loading");
    try {
      const page = await listRequests(next);
      setItems((current) => (next ? [...current, ...page.items] : page.items));
      setCursor(page.nextCursor);
      setState("ready");
    } catch {
      setState("error");
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is defined per mount and reads only stable props
  useEffect(() => {
    void load();
  }, []);

  return (
    <section aria-labelledby="requests-heading">
      <h2 id="requests-heading">Your requests</h2>
      <p>
        <a href="/requests/new">Raise a new request</a>
      </p>
      {state === "error" ? (
        <p role="alert">Your requests could not be loaded. Refresh to try again.</p>
      ) : null}
      {state === "ready" && items.length === 0 ? (
        <p>You have not raised any requests yet.</p>
      ) : null}
      <ul style={{ listStyle: "none", padding: 0 }}>
        {items.map((r) => (
          <li key={r.id} style={{ borderBottom: "1px solid #ddd", padding: "0.75rem 0" }}>
            <a href={`/requests/${r.publicId}`}>
              <strong>{r.publicId}</strong> {r.subject}
            </a>
            <br />
            <span>{STATUS_LABELS[r.status]}</span>
            {!r.onBoard && r.status !== "withdrawn" ? (
              <span style={{ marginLeft: "0.5rem", fontSize: "0.85rem", color: "#7a4b00" }}>
                Not yet on the support board
              </span>
            ) : null}
            <span style={{ marginLeft: "0.5rem", fontSize: "0.85rem", color: "#555" }}>
              {new Date(r.createdAt).toLocaleString()}
            </span>
          </li>
        ))}
      </ul>
      {cursor ? (
        <button type="button" onClick={() => void load(cursor)} disabled={state === "loading"}>
          Load more
        </button>
      ) : null}
      <p role="status" aria-live="polite">
        {state === "loading" ? "Loading…" : ""}
      </p>
    </section>
  );
}
